// src/features/dashboard/components/OverdueTasksWarning.jsx
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Typography,
  Button,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';

// Hooks
import { useTasks } from '../../tasks/hooks/useTasks';
import useCachedData from '../hooks/useCachedData';
import { CACHE_KEYS } from '../utils/dashboardCache';

const OverdueTasksWarning = React.memo(function OverdueTasksWarning({
  autoRefresh = true,
  refreshInterval = 300000, // 5 minutes
  maxDisplayTasks = 3,
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // API hooks
  const { getOverdueTasks, fetchTaskStatistics } = useTasks();

  // Cached overdue tasks data
  const {
    data: overdueTasksData,
    loading: overdueLoading
  } = useCachedData(
    CACHE_KEYS.OVERDUE_TASKS,
    useCallback(async () => {
      return await getOverdueTasks({ limit: maxDisplayTasks });
    }, [getOverdueTasks, maxDisplayTasks]),
    { autoRefresh, refreshInterval }
  );

  // Cached statistics data for overdue count
  const {
    data: statisticsData,
    loading: statsLoading
  } = useCachedData(
    CACHE_KEYS.OVERDUE_STATS,
    fetchTaskStatistics,
    { autoRefresh, refreshInterval }
  );

  const handleViewOverdue = useCallback(() => {
    navigate('/tasks?filter=overdue');
  }, [navigate]);

  // Extract data
  const overdueCount = statisticsData?.data?.overdue_tasks || 0;
  const overdueTasks = overdueTasksData?.data?.tasks || [];

  // Don't render if no overdue tasks or still loading initial data
  if ((!overdueCount || overdueCount === 0) && !overdueLoading && !statsLoading) {
    return null;
  }

  // Show loading state only if we have no cached data
  if ((overdueLoading || statsLoading) && !overdueTasksData && !statisticsData) {
    return null; // Or you could show a skeleton/loading state
  }

  // Get task titles for display
  const taskTitles = overdueTasks.map(task => task.title);
  const displayText = taskTitles.length > 0
    ? taskTitles.join(' | ')
    : t('common:dashboard.overdue_warning.multiple_attention');

  return (
    <Alert
      severity="error"
      sx={{
        mb: 3,
        '& .MuiAlert-message': { width: '100%' },
        border: '2px solid',
        borderRadius: "5px",
        borderColor: 'error.main',
      }}
    >
      <Box sx={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        width: '100%'
      }}>
        {/* Left side - Warning content */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Typography variant="subtitle2" fontWeight="bold" color="error.main">
              {t('common:dashboard.overdue_warning.urgent')}: {t('common:dashboard.overdue_warning.tasks_overdue', { count: overdueCount })}
            </Typography>
          </Box>
          <Typography
            variant="body2"
            sx={{
              color: 'inherit',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              opacity: 0.9
            }}
          >
            {displayText}
          </Typography>
        </Box>

        {/* Right side - Action button */}
        <Button
          variant="contained"
          size="small"
          onClick={handleViewOverdue}
          color='error'
          sx={{
            ml: 2,
            color: 'error.contrastText',
            flexShrink: 0,
            '&:hover': {
              bgcolor: 'error.main',
            }
          }}
        >
          {t('common:dashboard.overdue_warning.view_tasks')}
        </Button>
      </Box>
    </Alert>
  );
});

export default OverdueTasksWarning;